import { Selector } from '@ngxs/store';
import _ from 'lodash';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { SamwinCallListSelector } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.selector';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { IShortCallInfo, SamwinLinesState, SamwinLinesStateModel } from 'src/app/store/samwin/samwin-lines/samwin-lines.state';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SessionState } from 'src/app/store/session/session.state';
import { IciFlags } from 'src/generated/signalR/ici-flags';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';
import { SamwinUserShortInfoRemoteDto } from 'src/generated/signalR/samwin-user-short-info-remote-dto';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { ChatsSelectors } from '../chats/chats.selectors';
import { EmailBehaviourState } from '../email/email-behaviour/email-behaviour.state';
import { SkillGroup } from '../samwin-skills/skill-group';
import { SamwinUserConfigurationState } from '../samwin-user-configuration/samwin-user-configuration.state';

export class SamwinLinesStateSelector {
   @Selector([SamwinLinesState])
   public static getSourceLineState(state: SamwinLinesStateModel): LineStateEnum {
      if (_.isNil(state.sourceLineStatus) || _.isNil(state.sourceLineStatus.lineState)) {
         return LineStateEnum.IDLE;
      }
      return LineStateEnum.valueOf(state.sourceLineStatus.lineState);
   }

   @Selector([SamwinLinesState])
   public static getDestinationLineState(state: SamwinLinesStateModel): LineStateEnum {
      if (_.isNil(state.destinationLineStatus) || _.isNil(state.destinationLineStatus.lineState)) {
         return LineStateEnum.IDLE;
      }
      return LineStateEnum.valueOf(state.destinationLineStatus.lineState);
   }

   @Selector([SamwinLinesState])
   public static getDestinationLineNumber(state: SamwinLinesStateModel): string {
      return state?.destinationLineStatus?.dn ?? '';
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, SamwinLinesStateSelector.getDestinationLineState])
   public static canCallBeReQueued(lineSourceState: LineStateEnum, lineDestinationState: LineStateEnum): boolean {
      // TODO: shared skill handling
      if (lineDestinationState !== LineStateEnum.IDLE) {
         return false;
      }
      return lineSourceState === LineStateEnum.ACCEPTED || lineSourceState === LineStateEnum.CONNECTED || lineSourceState === LineStateEnum.ON_HOLD;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, SamwinLinesStateSelector.getDestinationLineState])
   public static canExecuteIvr(lineSourceState: LineStateEnum, lineDestinationState: LineStateEnum): boolean {
      if (lineDestinationState !== LineStateEnum.IDLE) {
         return false;
      }
      return lineSourceState === LineStateEnum.CONNECTED;
   }

   @Selector([SamwinLinesStateSelector.getDestinationLineState])
   public static canStartConference(lineDestinationState: LineStateEnum): boolean {
      // destination line being connected or on-hold implies source line being in a conferencable state
      return lineDestinationState === LineStateEnum.CONNECTED || lineDestinationState === LineStateEnum.ON_HOLD;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, SamwinLinesStateSelector.getDestinationLineState])
   public static isInConference(lineSourceState: LineStateEnum, lineDestinationState: LineStateEnum): boolean {
      return lineSourceState === LineStateEnum.CONFERENCED || lineDestinationState === LineStateEnum.CONFERENCED;
   }

   @Selector([SamwinLinesState])
   public static getCurrentIncomingCallDirectorySubscriberId(state: SamwinLinesStateModel): number {
      if (_.isNil(state?.sourceLineStatus?.fields?.id)) {
         return null;
      }
      return _.toNumber(state.sourceLineStatus.fields.id);
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState])
   public static isCallInProgress(lineSourceState: LineStateEnum): boolean {
      return lineSourceState.isCallInProgress;
   }

   @Selector([SamwinLinesStateSelector.isCallInProgress, SamwinLinesStateSelector.getActiveCallId, SamwinCallListState.getCallListFilteredBySkillFilter])
   public static getWaitingItems(isCallInProgress: boolean, activeCallId: number, callList: SamwinCallInfoRemoteDto[]) {
      if (!isCallInProgress) {
         return [];
      }
      return callList.filter((item) => item.callId !== activeCallId);
   }

   @Selector([SamwinLinesState])
   public static getCallDirection(state: SamwinLinesStateModel): number {
      if (state?.sourceLineStatus?.ici.ici & IciFlags.OUTBOUND) {
         return IciFlags.OUTBOUND;
      } else {
         return IciFlags.INBOUND;
      }
   }

   @Selector([SamwinLinesState, SamwinCallListSelector.getActiveOrRingingCallFromSharedSkill])
   public static getRingingCallId(state: SamwinLinesStateModel, activeOrRingingCallFromSharedSkill: IShortCallInfo): number {
      if (state?.sourceLineStatus && LineStateEnum.valueOf(state.sourceLineStatus.lineState)?.isRinging) {
         // ignore calls being answered
         if (state.sourceLineStatus.callId === state.callIdBeingAnswered) {
            return null;
         }

         // Do not treat outbound calls of tapi-less ACDs as ringing.
         if (state.sourceLineStatus.ici.ici & IciFlags.LOCAL_CALL) {
            return null;
         }
         return state.sourceLineStatus.callId;
      }
      if (!_.isNil(activeOrRingingCallFromSharedSkill)) {
         return activeOrRingingCallFromSharedSkill.callId;
      }
      return null;
   }

   @Selector([SamwinLinesState])
   public static getActiveCallId(state: SamwinLinesStateModel): number {
      if (state.sourceLineStatus && LineStateEnum.valueOf(state.sourceLineStatus.lineState).isCallInProgress) {
         return state.sourceLineStatus.callId;
      }
      return null;
   }

   @Selector([SamwinLinesStateSelector.getRingingCallId, SamwinUserStatusState.isAgentBusy])
   public static isPhoneRinging(ringingCallId: number, isAgentBusy: boolean): boolean {
      return !_.isNil(ringingCallId) && !isAgentBusy;
   }

   @Selector([SamwinLinesStateSelector.isPhoneRinging, SamwinLinesStateSelector.isCallInProgress])
   public static isPhoneRingingOrInProgress(isPhoneRinging: boolean, isCallInProgress: boolean): boolean {
      return isPhoneRinging || isCallInProgress;
   }

   @Selector([SamwinLinesStateSelector.isPhoneRinging, SamwinLinesStateSelector.isCallInProgress, SamwinLinesState.isAnswering])
   public static isPhoneRingingOrInProgressOrAnswering(isPhoneRinging: boolean, isCallInProgress: boolean, isAnswering: any): boolean {
      return isPhoneRinging || isCallInProgress || isAnswering;
   }

   @Selector([SamwinLinesState, SamwinCallListSelector.getActiveOrRingingCallFromSharedSkill])
   public static getCurrentCallShortInfo(state: SamwinLinesStateModel, activeOrRingingCallFromSharedSkill: IShortCallInfo): IShortCallInfo {
      const lineForShortInfo = _.isNil(state.sourceLineStatus) ? state.destinationLineStatus : state.sourceLineStatus;

      if (_.isNil(lineForShortInfo)) {
         return activeOrRingingCallFromSharedSkill;
      }
      const lineStateEnum = LineStateEnum.valueOf(state.sourceLineStatus.lineState);

      if (!lineStateEnum.isCallInProgress && !lineStateEnum.isRinging) {
         return activeOrRingingCallFromSharedSkill;
      }
      return <IShortCallInfo>{
         callId: lineForShortInfo.callId,
         peerId: lineForShortInfo.dn,
         peerName: lineForShortInfo.name,
      };
   }

   @Selector([SamwinLinesState])
   public static getRingingCallShortInfoWithoutSharedSkill(state: SamwinLinesStateModel): IShortCallInfo {
      const lineForShortInfo = _.isNil(state.sourceLineStatus) ? state.destinationLineStatus : state.sourceLineStatus;
      if (_.isNil(lineForShortInfo)) {
         return null;
      }
      const lineStateEnum = LineStateEnum.valueOf(lineForShortInfo.lineState);
      if (!_.isNil(lineForShortInfo) && lineStateEnum.isRinging) {
         return <IShortCallInfo>{
            callId: lineForShortInfo.callId,
            peerId: lineForShortInfo.dn,
            peerName: lineForShortInfo.name,
         };
      }
      return null;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_DISCONNECT_CALL_BEFORE_ANSWER)])
   public static canDisconnectLineA(sourceLineState: LineStateEnum, mayDeclineCalls: boolean): boolean {
      switch (sourceLineState) {
         case LineStateEnum.UNKNOWN:
         case LineStateEnum.IDLE:
            return false;
         case LineStateEnum.OFFERING:
         case LineStateEnum.ACCEPTED:
            return mayDeclineCalls;
         default:
            return true;
      }
   }

   @Selector([SamwinLinesStateSelector.getDestinationLineState])
   public static canDisconnectLineB(destinationLineStatus: LineStateEnum): boolean {
      return destinationLineStatus !== LineStateEnum.IDLE;
   }

   @Selector([SamwinLinesStateSelector.canDisconnectLineA, SamwinLinesStateSelector.canDisconnectLineB])
   public static canEndCall(canDisconnectLineA: boolean, canDisconnectLineB: boolean): boolean {
      return canDisconnectLineA || canDisconnectLineB;
   }

   @Selector([SamwinLinesState])
   public static isSourceLineStateParkable(state: SamwinLinesStateModel): boolean {
      if (_.isNil(state.sourceLineStatus)) {
         return false;
      }
      const lineState = LineStateEnum.valueOf(state.sourceLineStatus.lineState);
      return _.includes([LineStateEnum.CONNECTED, LineStateEnum.ON_HOLD, LineStateEnum.ACCEPTED], lineState);
   }

   @Selector([SamwinLinesStateSelector.isSourceLineStateParkable, SamwinLinesStateSelector.canDisconnectLineB])
   private static canCallBeParked(isSourceLineStateParkable: boolean, canDisconnectLineB: boolean): boolean {
      return isSourceLineStateParkable && !canDisconnectLineB;
   }

   @Selector([SamwinLinesStateSelector.canCallBeParked, EnabledFeaturesState.isFeatureEnabled(FeatureEnum.PARK_CALL_AT_AGENT)])
   public static canCallBeParkedAtAgent(canCallBeParked: boolean, isFeatureEnabled: boolean): boolean {
      return canCallBeParked && isFeatureEnabled;
   }

   @Selector([
      SamwinLinesStateSelector.canCallBeParked,
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.PARK_CALL_IN_SKILL),
      SamwinSkillsState.isCurrentCallInPrivateSkill,
   ])
   public static canCallBeParkedInSkill(canCallBeParked: boolean, isFeatureEnabled: boolean, isCurrentCallInPrivateSkill: boolean): boolean {
      return canCallBeParked && isFeatureEnabled && !isCurrentCallInPrivateSkill;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState])
   public static isSourceLineOnHold(state: LineStateEnum): boolean {
      return state === LineStateEnum.ON_HOLD;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, SamwinLinesStateSelector.getDestinationLineState])
   public static canToggleHoldLineA(sourceLineState: LineStateEnum, destinationLineState: LineStateEnum): boolean {
      const holdableStates = [LineStateEnum.ON_HOLD, LineStateEnum.CONNECTED, LineStateEnum.CONFERENCED];
      // hold on line A can be toggled when
      //  - line A is in a holdable state
      //  - or line A is in a pending transfer and line B is already in a holdable state
      return (
         _.includes(holdableStates, sourceLineState) ||
         (sourceLineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER && _.includes(holdableStates, destinationLineState))
      );
   }

   @Selector([SamwinLinesStateSelector.getDestinationLineState])
   public static canToggleHoldLineB(destinationLineState: LineStateEnum): boolean {
      const holdableStates = [LineStateEnum.ON_HOLD, LineStateEnum.CONNECTED, LineStateEnum.CONFERENCED];
      return _.includes(holdableStates, destinationLineState);
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState])
   public static isLineAOnHold(lineState: LineStateEnum): boolean {
      return lineState === LineStateEnum.ON_HOLD || lineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER;
   }

   @Selector([SamwinLinesStateSelector.getDestinationLineState])
   public static isLineBOnHold(lineState: LineStateEnum): boolean {
      return lineState === LineStateEnum.ON_HOLD || lineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER;
   }

   @Selector([SamwinLinesStateSelector.getSourceLineState, SamwinLinesStateSelector.getDestinationLineState])
   public static canSwitchHoldLines(sourceLineState: LineStateEnum, destinationLineState: LineStateEnum): boolean {
      const sourceLineOnHold = sourceLineState === LineStateEnum.ON_HOLD || sourceLineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER;
      const destinationLineOnHold = destinationLineState === LineStateEnum.ON_HOLD || sourceLineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER;
      if (sourceLineOnHold && destinationLineState === LineStateEnum.CONNECTED) {
         return true;
      }
      if (destinationLineOnHold && sourceLineState === LineStateEnum.CONNECTED) {
         return true;
      }
      return false;
   }

   @Selector([SamwinLinesStateSelector.getCurrentCall])
   public static isCallBeingRecorded(call: SamwinCallInfoRemoteDto): boolean {
      if (_.isNil(call)) return false;

      return Boolean(call.ici.ici & IciFlags.RECORDING);
   }

   @Selector([
      SamwinLinesState,
      SamwinLinesStateSelector.isCallBeingRecorded,
      EnabledFeaturesState.allowedToManuallyRecord,
      SamwinSkillsState.getSkillConfigurationOfCurrentCall,
   ])
   public static canToggleManualCallRecording(
      state: SamwinLinesStateModel,
      isRecording: boolean,
      allowedToManuallyRecord: boolean,
      skillConfiguration: SkillConfigurationRemoteDto
   ): boolean {
      if (!allowedToManuallyRecord) {
         return false;
      }

      // stopping is always possible
      if (isRecording) return true;

      // no recording if there is no call
      if (_.isNil(state.sourceLineStatus)) {
         return false;
      }

      const lineStateA = LineStateEnum.valueOf(state.sourceLineStatus.lineState);

      // when a recording announcement is configured, recording can only be started when talking to line A
      if (skillConfiguration?.hasRecordingAnnouncement) {
         // talking on line A?
         return _.includes([LineStateEnum.CONNECTED, LineStateEnum.CONFERENCED], lineStateA);
      }

      // if no announcement is configured, recording can be started at "any" time (if there is a call)
      return lineStateA.isConnected;
   }

   @Selector([SamwinLinesStateSelector.getCurrentCall])
   public static isCallMarkedAsMalicious(call: SamwinCallInfoRemoteDto): boolean {
      if (!_.isNil(call)) {
         return Boolean(call.ici.ici & IciFlags.EMERGENCY);
      }
      return false;
   }

   @Selector([SamwinLinesState, SamwinLinesStateSelector.isCallMarkedAsMalicious])
   public static canMarkMaliciousCall(state: SamwinLinesStateModel, isCallMarkedAsMalicious: boolean): boolean {
      if (_.isNil(state.sourceLineStatus)) {
         return false;
      }

      const lineState = LineStateEnum.valueOf(state.sourceLineStatus.lineState);
      return (
         !isCallMarkedAsMalicious &&
         (lineState === LineStateEnum.CONNECTED ||
            lineState === LineStateEnum.ON_HOLD ||
            lineState === LineStateEnum.CONFERENCED ||
            lineState === LineStateEnum.ON_HOLD_PENDING_TRANSFER ||
            lineState === LineStateEnum.ON_HOLD_PENDING_CONFERENCE)
      );
   }

   @Selector([SamwinLinesState])
   public static getSourceLineStateModel(state: SamwinLinesStateModel): SamwinLineStatusRemoteDto {
      return _.isNil(state.sourceLineStatus) ? null : state.sourceLineStatus;
   }

   @Selector([SamwinLinesState])
   public static getDestinationLineStateModel(state: SamwinLinesStateModel): SamwinLineStatusRemoteDto {
      return _.isNil(state.destinationLineStatus) ? null : state.destinationLineStatus;
   }

   @Selector([SamwinLinesState, SamwinCallListState.getCallByIdFn])
   public static getCurrentCall(lineState: SamwinLinesStateModel, getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto): SamwinCallInfoRemoteDto {
      let callInfo: SamwinCallInfoRemoteDto;
      if (!_.isNil(lineState.sourceLineStatus)) {
         callInfo = getCallByIdFn(lineState.sourceLineStatus.callId);
      }
      return callInfo;
   }

   @Selector([SamwinLinesStateSelector.getCurrentCall, SamwinCallListSelector.getActiveOrRingingCallFromSharedSkill, SamwinCallListState.getCallByIdFn])
   public static getActiveOrRingingCall(
      currentCall: SamwinCallInfoRemoteDto,
      activeOrRingingCallFromSharedSkill: IShortCallInfo,
      getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto
   ): SamwinCallInfoRemoteDto {
      if (!_.isNil(currentCall)) {
         return currentCall;
      }
      if (!_.isNil(activeOrRingingCallFromSharedSkill)) {
         return getCallByIdFn(activeOrRingingCallFromSharedSkill.callId);
      }
      return null;
   }

   @Selector([ChatsSelectors.isAnyActiveChats, SamwinLinesStateSelector.isCallInProgress, EmailBehaviourState.isAnyEmailsInProgress])
   public static isAnyLineActivityInProgress(isAnyActiveChats: boolean, isCallInProgress: boolean, isAnyEmailsInProgress: boolean) {
      return isAnyActiveChats || isCallInProgress || isAnyEmailsInProgress;
   }

   @Selector([SamwinLinesStateSelector.getActiveOrRingingCall, SamwinCallListState.getCallInfoFromPreviousCall, SamwinUserStatusState.isUserStateAutoACW])
   public static getCallerInfoFromCurrentOrPreviousCall(
      activeCall: SamwinCallInfoRemoteDto,
      previousCall: SamwinCallInfoRemoteDto,
      isUserStateAutoACW: boolean
   ): SamwinCallInfoRemoteDto {
      return isUserStateAutoACW ? previousCall : activeCall;
   }

   @Selector([SamwinCallListState.getCallList, SamwinLinesStateSelector.getActiveOrRingingCall])
   public static waitingItems(callList: SamwinCallInfoRemoteDto[], activeOrRingingCall: SamwinCallInfoRemoteDto) {
      const inboundCallList = callList.filter((c) => c.ici.ici !== IciFlags.OUTBOUND);
      if (activeOrRingingCall) {
         return inboundCallList.filter((c) => c?.callId !== activeOrRingingCall?.callId);
      }

      if (inboundCallList.length > 1) {
         return inboundCallList;
      }

      return [];
   }

   @Selector([SamwinSkillsState.skillConfigurationsForWaitingItems, SamwinLinesStateSelector.waitingItems, SamwinSkillsState.getSkillGroupById])
   public static skillsOfWaitingItems(
      skillsForNotification: SkillConfigurationRemoteDto[],
      callList: SamwinCallInfoRemoteDto[],
      getSkillGroupById: (skillId: number) => SkillGroup
   ) {
      const skillIds = skillsForNotification.map((skill) => skill.id);

      return callList.map((call) => getSkillGroupById(call.queueId)).filter((skillGroup) => skillGroup && skillIds.includes(skillGroup.skillId));
   }

   @Selector([SamwinLinesStateSelector.skillsOfWaitingItems])
   public static isItemWaiting(listOfWaitingItems: SkillGroup[]) {
      return listOfWaitingItems.length > 0;
   }

   @Selector([SessionState.getAuthenticatedUserId, SamwinUserConfigurationState.getUserConfigurationFn])
   public static waitingItemNotificationType(userId: number, getUserConfig: (userId: number) => SamwinUserShortInfoRemoteDto) {
      return getUserConfig(userId)?.notificationType;
   }
}
